import React, { useCallback, useMemo } from "react";

import Event from "@mui/icons-material/Event";
import { DateTime } from "luxon";

import { DATE_TIME_FORMAT } from "@waterly/utils-isomorphic";

import { BasicDateTimePicker, DatePickerActionBar, type DatePickerActionBarProps } from "../../pickers";

import type { MetricInputProps } from "./DataEntryInput.types";
import { InputMode } from "./DataEntryInput.types";
import { loadingInputSx } from "./loadingInputSx";
import { useInputSx } from "./useInputSx";

export type TimeStampInputProps = MetricInputProps & {
  isLoading: boolean;
  timeZone: string;
};

const noOp = () => undefined;

export const TimeStampInput: React.FC<TimeStampInputProps> = ({
  onChange,
  value,
  isDirty,
  mode,
  isLoading,
  timeZone,
}) => {
  const inputDisabled = mode === InputMode.ReadOnly || mode === InputMode.SiteEdit || isLoading;
  const inputReadOnly = inputDisabled || mode === InputMode.Loading;
  const inputSx = useInputSx({ mode, isDirty });

  const dateTimeValue = useMemo(() => {
    if (!value || value === "") {
      return null;
    }
    return DateTime.fromFormat(value, DATE_TIME_FORMAT, { zone: timeZone });
  }, [timeZone, value]);

  const handleAccept = useCallback(
    (date: DateTime | null) => {
      if (!date) {
        return onChange("");
      }
      onChange(date.toFormat(DATE_TIME_FORMAT));
    },
    [onChange],
  );

  const handleSetNow = useCallback(() => {
    const now = DateTime.now().setZone(timeZone);
    handleAccept(now);
  }, [handleAccept, timeZone]);

  const actionBar = useCallback(
    (props: DatePickerActionBarProps) => {
      return <DatePickerActionBar {...props} onSetToday={handleSetNow} />;
    },
    [handleSetNow],
  );

  return (
    <BasicDateTimePicker
      value={dateTimeValue}
      onAccept={handleAccept}
      onChange={noOp}
      disabled={inputDisabled}
      sx={isLoading ? loadingInputSx : undefined}
      slotProps={{
        actionBar: {
          actions: ["cancel", "clear", "today", "accept"],
        },
        textField: {
          fullWidth: true,
          InputProps: {
            placeholder: "",
            sx: inputSx,
            readOnly: inputReadOnly,
            endAdornment: <Event />,
          },
          inputProps: {
            "data-component": "MetricInput",
          },
        },
      }}
      slots={{
        actionBar: actionBar,
      }}
    />
  );
};
